import {
  getInfoPassport as getInfoPassportRepository,
  getUserInfo as getUserInfoRepository,
  registerInfo as registerInfoRepository,
  registerPhoto as registerPhotoRepository,
  updateUserInfo as updateUserInfoRepository,
  getUserFollowers as getUserFollowersRepository,
  getUserPrivacySettings as getUserPrivacySettingsRepository,
  getUserPrivacyConfiguration as getUserPrivacyConfigurationRepository,
  blockUser as blockUserRepository,
  followUser as followUserRepository,
  unFollowUser as unFollowUserRepository,
  updatePrivacySettings as updatePrivacySettingsRepository,
  updateUserPhoto as updateUserPhotoRepository, 
  postEditPublication as postEditPublicationRepository,
  deleteAccount as deleteAccountRepository,
  getStampCatalog as getStampCatalogRepository,
  getUserStamps as getUserStampsRepository,
  postGenerateStamp as postGenerateStampRepository,
  getStampUserRelationship as getStampUserRelationshipRepository,
  getUserGeolocation,
  getProcessUser as getProcessUserRepository,
  getStatusUser as getStatusUserRepository,
  getOccupationsCatalog as getOccupationsCatalogRepository,
} from "../repositories/UserInfoRepository";
import {saveUserInfo} from "../mappers/UserInfoMapper";

// Cache compartida para /api/ob/usr/info.
// Evita que Feed, comentarios, perfil, conexiones, etc. consulten el mismo
// usuario en paralelo o repetidamente durante una ventana corta.
const USER_INFO_CACHE_TTL_MS = 5 * 60 * 1000;
const userInfoCache = new Map();
const userInfoInFlight = new Map();

const extractUserId = (value) => {
  if (value === undefined || value === null) return null;

  if (typeof value === "string" || typeof value === "number") {
    const normalized = String(value).trim();
    return normalized || null;
  }

  // updateUserInfoService recibe el FormData que se envia al backend (ver
  // SettingsPersonalForm.buildUpdateReq), no un id plano. Sus valores no son
  // propiedades del objeto (se leen con .get), asi que sin este caso la
  // invalidacion de cache siempre fallaba en silencio.
  if (typeof FormData !== "undefined" && value instanceof FormData) {
    const candidate =
      value.get("IdUsuario") ??
      value.get("idUsuario") ??
      value.get("userId") ??
      value.get("idUser") ??
      value.get("id");

    if (candidate === undefined || candidate === null || candidate === "") {
      return null;
    }
    const normalized = String(candidate).trim();
    return normalized || null;
  }

  if (typeof value === "object") {
    const candidate =
      value.IdUsuario ??
      value.idUsuario ??
      value.userId ??
      value.idUser ??
      value.id;

    if (candidate === undefined || candidate === null) return null;
    const normalized = String(candidate).trim();
    return normalized || null;
  }

  return null;
};

export const invalidateUserInfoCache = (userId) => {
  const normalizedUserId = extractUserId(userId);
  if (!normalizedUserId) return;

  userInfoCache.delete(normalizedUserId);
  userInfoInFlight.delete(normalizedUserId);
};

export const clearUserInfoCache = () => {
  userInfoCache.clear();
  userInfoInFlight.clear();
};

const getCachedUserInfo = (userId) => {
  const cached = userInfoCache.get(userId);
  if (!cached) return null;

  if (cached.expiresAt <= Date.now()) {
    userInfoCache.delete(userId);
    return null;
  }

  return cached.data;
};

export const registerInfoService = async (userInfo) => {
  try {
    const response = await registerInfoRepository(userInfo);
    invalidateUserInfoCache(userInfo);
    return saveUserInfo(response);
  } catch (error) {
    throw error;
  }
}

export const registerPhotoService = async (photoUser) => {
  try {
    const response = await registerPhotoRepository(photoUser);
    invalidateUserInfoCache(photoUser);
    return response;
  } catch (error) {
    throw error;
  }
}

export const registerPassportService = async (userId)=> {
  try {
    const response = await getInfoPassportRepository(userId)
    return response.data
  } catch (error) {
    throw error
  }
}


export const getUserInfoService = async (userId, { forceRefresh = false } = {}) => {
  const normalizedUserId = extractUserId(userId);

  // Conserva el comportamiento anterior para valores no reconocibles.
  // No se cachean para no compartir accidentalmente una respuesta inválida.
  if (!normalizedUserId) {
    return getUserInfoRepository(userId);
  }

  if (!forceRefresh) {
    const cached = getCachedUserInfo(normalizedUserId);
    if (cached) return cached;

    const pending = userInfoInFlight.get(normalizedUserId);
    if (pending) return pending;
  }

  const request = getUserInfoRepository(normalizedUserId)
    .then((response) => {
      userInfoCache.set(normalizedUserId, {
        data: response,
        expiresAt: Date.now() + USER_INFO_CACHE_TTL_MS,
      });
      return response;
    })
    .catch((error) => {
      userInfoCache.delete(normalizedUserId);
      throw error;
    })
    .finally(() => {
      if (userInfoInFlight.get(normalizedUserId) === request) {
        userInfoInFlight.delete(normalizedUserId);
      }
    });

  userInfoInFlight.set(normalizedUserId, request);
  return request;
}

// export const getUserInfoService = async (userId, jwt) => {
//   try {
//     return await getUserInfoRepository(userId, jwt)
//   } catch (error) {
//     throw error;
//   }
// }

export const getUserPhotoService = async (userId, options = {}) => {
  try {
    // La fotografía vive en el mismo endpoint que getUserInfoService.
    // Reutilizar esa respuesta evita un segundo GET del mismo usuario.
    const response = await getUserInfoService(userId, options);
    const userInfo = response?.data ?? response ?? {};
    const fotoPerfilUrl =
      userInfo?.fotoPerfilUrl ??
      userInfo?.FotoPerfilUrl ??
      userInfo?.profilePic ??
      userInfo?.imagenBase64 ??
      userInfo?.fotoPerfilBase64 ??
      null;

    return {
      ...userInfo,
      content: fotoPerfilUrl,
    };
  } catch (error) {
    throw error;
  }
}

export const updateUserPhotoService = async (userId) => {
  try {
    const response = await updateUserPhotoRepository(userId);
    invalidateUserInfoCache(userId);
    return response;
  } catch (error) {
    throw error;
  }
};


export const updateUserInfoService = async (userId) => {
  try {
    const response = await updateUserInfoRepository(userId);
    invalidateUserInfoCache(userId);
    return response;
  } catch (error) {
    throw error;
  }
}

export const getOccupationsCatalogService = async () => {
  try {
    return await getOccupationsCatalogRepository()
  } catch (error) {
    throw error
  }
}

export const getUserFollowersService = async (userId) => {
  try {
    return await getUserFollowersRepository(userId)
  } catch (error) {
    throw error
  }
}

export const getUserPrivacySettingsService = async (userId) => {
  try {
    return await getUserPrivacySettingsRepository(userId)
  } catch (error) {
    throw error
  }
}

export const getUserPrivacyConfigurationService = async (userId) => {
  try {
    return await getUserPrivacyConfigurationRepository(userId)
  } catch (error) {
    throw error
  }
}

export const blockUserService = async (req) => {
  try {
    return await blockUserRepository(req)
  } catch (error) {
    throw error
  }
}

export const followUserService = async (req) => {
  try {
    return await followUserRepository(req)
  } catch (error) {
    throw error
  }
}

export const unFollowUserService = async (req) => {
  try {
    return await unFollowUserRepository(req)
  } catch (error) {
    throw error
  }
}

export const updatePrivacySettingsService = async (req) => {
  try {
    return await updatePrivacySettingsRepository(req)
  } catch (error) {
    throw error
  }
}

export const postEditPublicationService = async (req) => {
  try {
    return await postEditPublicationRepository(req)
  } catch (error) {
    throw error
  }
}

//Eliminar cuenta
export const deleteAccountService = async (data) => {
  try {
    const response = await deleteAccountRepository(data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Obtener catálogo de sellos
export const getStampCatalogService = async () => {
  try {
    const response = await getStampCatalogRepository();
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Obtener sellos de usuario
export const getUserStampsService = async (idUsuario) => {
  try {
    const response = await getUserStampsRepository(idUsuario);
    return response;
  } catch (error) {
    throw error;
  }
};

// Generar sello
export const postGenerateStampService = async (data) => {
  try {
    const response = await postGenerateStampRepository(data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Obtener relación sello/usuario
export const getStampUserRelationshipService = async (payload) => {
  try {
    const response = await getStampUserRelationshipRepository(payload);
    return response;
  } catch (error) {
    throw error;
  }
};

// Geolocalización
export const getUserGeolocationService = async (payload) => {
  try {
    const response = await getUserGeolocation(payload);
    return response;
  } catch (error) {
    throw error;
  }
};

// Obtener proceso del usuario
export const getProcessUserService = async (idSolicitud) => {
  try {
    const response = await getProcessUserRepository(idSolicitud);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Obtener el estatus donde se quedo
export const getStatusUserService = async (idUsuario) => {
  try {
    const response = await getStatusUserRepository(idUsuario);
    return response.data;
  } catch (error) {
    throw error;
  }
};
